<template>
  <component
    :is="component"
    type="button"
    :to="to"
    :disabled="disabled"
    :class="['list-group-item', $attrs.class, sizeClass, { 'is-active': isRouteActive }]"
    active-class="is-active"
    @click="handleClick"
  >
    <slot name="prefix" />
    <slot />
    <slot name="suffix" />
  </component>
</template>

<script setup>
import { computed } from 'vue';
import { RouterLink, useRoute } from 'vue-router';

const route = useRoute();
const emit = defineEmits(['click']);

const props = defineProps({
  to: {
    type: [String, Object],
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['xs', 'sm', 'md', 'lg'].includes(value),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  onNavigate: {
    type: Function,
    default: null,
  },
});

const path = computed(() => props.to?.path || props.to || null);

const isRouteActive = computed(() => {
  if (props.to && typeof path.value === 'string') {
    return route.path.startsWith(path.value);
  } else {
    return false;
  }
});

function handleClick() {
  if (!props.disabled) {
    emit('click');
  }
  if (props.onNavigate) {
    props.onNavigate();
  }
}

const component = computed(() => (props.to ? RouterLink : 'button'));

const sizeClass = computed(() => {
  const classes = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };
  return classes[props.size];
});
</script>

<style>
@reference '@/assets/main.css';

.list-group-item {
  @apply w-full flex items-center gap-2 py-2 px-3 font-medium text-gray-700 rounded-md transition-colors duration-200;

  &:not(:disabled) {
    @apply cursor-pointer hover:text-gray-800 hover:bg-gray-100;
  }

  &:disabled {
    @apply cursor-not-allowed text-gray-500;
  }

  &.is-active {
    @apply bg-gray-100 text-gray-800;
  }
}
</style>
